import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import { Button, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import usePromoteDraftCart from '../../hooks/draftCart/usePromoteDraftCart.hooks';
import { selectShouldShowMyCart } from '../../redux/features/agentMode/agentMode.selector';
import DraftCartItemReview from '../OrderReview/DraftCartItemReview';
import { DraftCartScope } from '../OrderReview/CartScopeContext';
import {
  selectDraftCartItemCollapseStates,
  selectDraftCartItemCount,
  selectDraftCartItems,
  selectDraftCartItemsQuantity,
} from '../../redux/features/draftCart/draftCart.selector';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import { CART_ACTION_BUTTON_SX } from './cartActionButton';
import ConsoleCard from './ConsoleCard';

export const MY_CART_TITLE = 'My Cart';
/**
 * HITL-1649 — an empty My Cart says nothing at all.
 *
 * It used to carry three pieces of standing text: `Nothing here yet.` in the
 * body, `0 items` in the header, and a permanent `· not sent` beside it. All
 * three describe the absence of something, on a console where the agent is
 * reading two carts at once with a customer talking at them, and none of them
 * tells them anything the empty column has not already said.
 *
 * `not sent` went with them rather than being kept "for safety". It was written
 * (HITL-1614) when the other column was called `AI Cart` and the question "which
 * of these is the customer getting?" genuinely needed answering in the header.
 * HITL-1647 renamed that column `LIVE ORDER`, which answers it in the one place
 * an agent is already looking, permanently and without a second caption
 * shouting it from the cart that is NOT the order.
 *
 * The count stays once there is something to count — `3 items` is information;
 * `0 items` is the same absence written a third way.
 */
/**
 * HITL-1645 — the label carries the warning, because there is no dialog.
 *
 * "Take over", not "Replace order": the press does both, and the one the agent
 * needs to weigh is the takeover. It stands the AI down for the rest of the
 * session and cannot be undone — a modal saying so would land in the seconds
 * before readback, which is where friction costs most, so the button says it
 * instead. It also names the mode the agent ends up in, which is the step they
 * no longer have to take themselves.
 *
 * HITL-1649 gave it `KeyboardDoubleArrowRightIcon` — `>>` — the mirror of the
 * `<<` on `Copy all to My Cart`. My Cart is the left column, so the lines really
 * do travel right into the Live order, and the two buttons now read as the two
 * directions of one movement rather than as two unrelated actions.
 *
 * `endIcon`, not `startIcon`: each chevron sits on the side of the label it
 * points at, so the pair reads outward from the gap between the two columns —
 *
 *     [ Take over with My Cart >> ] | [ << Copy all to My Cart ]
 *
 * — and a leading `>>` here would have pointed right while sitting on the left
 * edge of the button, back at the column it is leaving.
 */
export const TAKE_OVER_LABEL = 'Take over with My Cart';

const useStyles = makeStyles<Theme>(() => ({
  // Mirrors AgentCartCard's count chip.
  meta: {
    color: Colors.silverChalice,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  itemsScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: `0 ${HITL3_PADDING}`,
  },
  // HITL-1615: the cart-level promote. Pinned under the body so it stays
  // reachable however the column is scrolled — the same reasoning that puts the
  // totals in RightSidebar's footer.
  promoteFooter: {
    flexShrink: 0,
    padding: HITL3_PADDING,
    borderTop: `1px solid ${Colors.alto}`,
  },
}));

interface MyCartCardProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * HITL-1614 — MY CART, the agent's own column beside the AI's.
 *
 * Everything here reads `draftCart` (HITL-1611), the slice the AI's hypothesis
 * rebuild cannot reach. Nothing here dispatches `sendOrder`, emits an
 * interception or speaks, which is why its controls stay live in `Shadow` while
 * the rest of the console is refusing the agent's actions.
 *
 * Which of the two carts the customer is getting is answered by the OTHER
 * column's name — `LIVE ORDER` (HITL-1647) — rather than by a caption on this
 * one. See `MY_CART_TITLE` above for why the standing `not sent` went with the
 * rest of the empty-state text in HITL-1649.
 */
const MyCartCard = ({ open: openProp, onOpenChange }: MyCartCardProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const draftCartItems = useAppSelector(selectDraftCartItems);
  const quantities = useAppSelector(selectDraftCartItemsQuantity);
  const collapseStates = useAppSelector(selectDraftCartItemCollapseStates);
  const itemCount = useAppSelector(selectDraftCartItemCount);

  // HITL-1645: the takeover is offered in BOTH modes, because entering
  // `Intercept` is now the button's own first step rather than something the
  // agent has to do before pressing it. `selectShouldShowMyCart` is exactly
  // "this column is on screen", which is when the control belongs with it.
  const canTakeOver = useAppSelector(selectShouldShowMyCart);
  const { takeOverWithMyCart } = usePromoteDraftCart();

  const itemIds = Object.keys(draftCartItems ?? {})
    .map(Number)
    .sort((a, b) => a - b);

  // HITL-1645: the one way out of My Cart, in either mode, and only with
  // something to take over WITH. Replace — not append — is what avoids the
  // duplicate trap: an agent who shadowed the whole order would otherwise double
  // every line, which is exactly what the per-line promote did before it was
  // removed.
  const promoteFooter = canTakeOver ? (
    <div className={classes.promoteFooter}>
      <Button
        sx={CART_ACTION_BUTTON_SX}
        variant="contained"
        size="small"
        disableElevation
        endIcon={<KeyboardDoubleArrowRightIcon />}
        data-testid="take-over-with-my-cart"
        disabled={!itemIds.length}
        onClick={takeOverWithMyCart}
      >
        {TAKE_OVER_LABEL}
      </Button>
    </div>
  ) : undefined;

  return (
    <ConsoleCard
      footer={promoteFooter}
      icon={<ShoppingBasketOutlinedIcon />}
      title={MY_CART_TITLE}
      open={open}
      onToggle={toggleOpen}
      testId="my-cart-card"
      headerRight={
        // Nothing at all while the cart is empty: `0 items` is the empty column
        // restated, and the chevron then sits alone against the title exactly as
        // it does on a card with no count.
        itemCount ? (
          <span className={classes.meta}>
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
        ) : undefined
      }
    >
      <div className={classes.itemsScroll} data-testid="my-cart-items">
        {/*
          The scope provider is the id-collision fix: both slices number
          `cartItemId` from 1 and `dialogSlice.selectedItemCartId` is a single
          number, so without it a main-cart selection would also highlight and
          scroll the draft row sharing that id.
        */}
        <DraftCartScope>
          {itemIds.map((cartItemId) => (
            <DraftCartItemReview
              key={cartItemId}
              cartItem={draftCartItems[cartItemId]}
              quantity={quantities?.[cartItemId] ?? 1}
              collapseState={collapseStates?.[cartItemId]}
            />
          ))}
        </DraftCartScope>
      </div>
    </ConsoleCard>
  );
};

export default MyCartCard;
